import { useTheme } from '@mui/styles';
import { keycloakService } from '@tassat/authentication';
import { AuthorizationSuccessResponse, SecondaryApiTypeEnum } from '@tassat/common-definitions';
import React, { Suspense } from 'react';
import { Provider } from 'react-redux';
import {
    Redirect, Route, Router, Switch
} from 'react-router-dom';

import Client from 'Containers/client/client';
import AddressBook from 'Containers/client/pages/addressBook';
import ApiConnections from 'Containers/client/pages/apiConnections';
import DocumentCenter from 'Containers/client/pages/documentCenter';
import FirmHistory from 'Containers/client/pages/firmHistory';
import TermsOfUse from 'Containers/client/pages/termsOfUse';
import WalletsDashboard from 'Containers/client/pages/wallets';
import WalletView from 'Containers/client/pages/walletView';
import InvalidUser from 'Containers/common/invalidUser';
import SSOLogout from 'Containers/common/logout';
import PageLoading from 'Containers/common/pageLoading';
import PendingSetup from 'Containers/common/pendingSetup';
import SSOUserRequestCreated from 'Containers/common/ssoUserRequestCreated';
import Support from 'Containers/support/support';
import authService from 'Lib/auth/AuthService';
import InactivityTimer from 'Lib/auth/InactivityTimer';
import { handleAuthentication, handleCodeAuthentication } from 'Lib/authHelper';
import history from 'Lib/browserHistory';
import { RouteChangeTracker } from 'Lib/googleAnalytics/UserTracker';
import { getLoginMethod } from 'Lib/loginMethodFn';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import Worker from 'Lib/worker/PrimusWorker';
import clientStore from 'Reducers/store/clientStore';
import { useAppSelector } from 'Reducers/store/hooks';
import { getUser } from 'Selectors/common';
import { getApiTypesByUserId } from 'Selectors/userApiTypes';
import Logout from 'SharedComponents/auth/Logout';
import { ProtectedClientRoute as ProtectedRoute } from 'SharedComponents/auth/ProtectedRoute';

import LockedUser from './containers/common/lockedUser';
import Offline from './containers/common/offline';
import UnauthorizedFirm from './containers/common/unauthorizedFirm';

const ClientRouteList = () => {
    const user = useAppSelector(getUser);
    const userApiTypes = useAppSelector((state) => getApiTypesByUserId(state, false, state.user?.UserId || ''));
    const muiTheme = useTheme<ApplicationMuiTheme>();

    const authSSO = muiTheme?.app.clientTheme.features.Authentication.SSO;
    const secondaryApiEnabled = muiTheme?.app.clientTheme.features.Fred?.Fireblocks.API;

    return (
        <Router history={history}>
            <>
                <InactivityTimer
                    locationPath={history.location?.pathname}
                    logout={() => {
                        Worker.getInstance()
                            .inactivityLogout();
                        authService.logout();
                    }}
                />
                <RouteChangeTracker isAdmin={false} />
                <Switch>
                    {!authSSO &&
                        <Route
                            path='/auth_login'
                            render={() => {
                            getLoginMethod(false, muiTheme);

                            return null;
                        }}
                        />}
                    <Route
                        path='/invalid'
                        render={() => <InvalidUser />}
                    />
                    <Route
                        path='/requestCreated'
                        render={() => <SSOUserRequestCreated />}
                    />
                    <Route
                        path='/pendingSetup'
                        render={() => <PendingSetup />}
                    />
                    <Route
                        path='/ssoLogout'
                        render={() => <SSOLogout />}
                    />
                    <Route
                        path='/backchannelLogout'
                        render={(props) => {
                            keycloakService.backchannelLogout(false);

                            return null;
                        }}
                    />
                    <ProtectedRoute
                        component={TermsOfUse}
                        path='/tos'
                    />
                    <ProtectedRoute
                        component={() => <Redirect to='/account' />}
                        path='/login'
                    />
                    <Route
                        path='/callback'
                        render={(props) => {
                            handleAuthentication(props, false, muiTheme);

                            return null;
                        }}
                    />
                    <Route
                        path='/callback_code'
                        render={(props) => {
                            handleCodeAuthentication(props);

                            return null;
                        }}
                    />
                    {/**Following route handles silent login functionality with any
                     * Idp service iff there is an existing session with an oidc client. */}
                    <Route
                        path='/silent_login'
                        render={() => {
                            keycloakService.handleAuthentication(false, {} as AuthorizationSuccessResponse);

                            return <PageLoading />;
                        }}
                    />
                    <Route
                        component={Logout}
                        path='/logout'
                    />
                    <Route
                        component={LockedUser}
                        exact
                        path='/locked'
                    />
                    <Route
                        component={UnauthorizedFirm}
                        exact
                        path='/unauthorized_firm'
                    />
                    <Route
                        component={Offline}
                        exact
                        path='/offline'
                    />
                    <Route
                        path='/'
                        render={() => {
                            return (
                                <Client>
                                    <Suspense fallback={<PageLoading />}>
                                        <Switch>
                                            <ProtectedRoute
                                                component={FirmHistory}
                                                exact
                                                path='/accounts/history'
                                            />
                                            <ProtectedRoute
                                                component={WalletView}
                                                exact
                                                path='/accounts/:walletId'
                                            />
                                            <ProtectedRoute
                                                component={WalletsDashboard}
                                                exact
                                                path='/accounts'
                                            />
                                            <ProtectedRoute
                                                component={AddressBook}
                                                exact
                                                path='/addressbook'
                                            />
                                            <ProtectedRoute
                                                component={DocumentCenter}
                                                exact
                                                path='/documentcenter'
                                            />
                                            <ProtectedRoute
                                                component={Support}
                                                exact
                                                path='/support'
                                            />
                                            {user &&
                                                (
                                                user.IsTokenAPIUser ||
                                                secondaryApiEnabled &&
                                                userApiTypes.some(
                                                    (apiType) => apiType.APISecondaryConnectionType === SecondaryApiTypeEnum.FIREBLOCKS
                                                )
                                            ) &&
                                            <ProtectedRoute
                                                component={ApiConnections}
                                                exact
                                                path='/api'
                                            />}
                                            <Route
                                                component={() => <Redirect to='/accounts' />}
                                                path='/'
                                            />
                                        </Switch>
                                    </Suspense>
                                </Client>
                            );
                        }}
                    />
                    {authSSO ? <Route component={() => <Redirect to='/accounts' />} /> : <Route component={() => <Redirect to='/auth_login' />} />}
                </Switch>
            </>
        </Router>
    );
};

const Routes = () => (
    <Provider store={clientStore}>
        <ClientRouteList />
    </Provider>
);

export default Routes;